import React from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import { useCaseCollaboration } from './CaseCollaborationContext';
import { initialsForCollaborator } from './colors';

const MAX_VISIBLE = 3;

/**
 * Stacked initials circles for everyone currently editing a juror.
 * Shows up to MAX_VISIBLE avatars, then a "+" circle for the rest.
 * Hover lists every editor's name and email.
 */
export default function JurorEditingBadge({ jurorId, size = 20 }) {
  const { getEditorsForJuror, getLiveStatesForJuror } = useCaseCollaboration();

  // Anyone announced as editing OR actively typing counts as an editor,
  // so the badge still shows if one of the signals was missed.
  const editors = React.useMemo(() => {
    const bySession = new Map();
    for (const editor of getEditorsForJuror(jurorId)) {
      bySession.set(editor.sessionId || editor.email, editor);
    }
    for (const state of getLiveStatesForJuror(jurorId)) {
      const key = state.sessionId || state.email;
      if (!bySession.has(key)) {
        bySession.set(key, state);
      }
    }
    return [...bySession.values()];
  }, [getEditorsForJuror, getLiveStatesForJuror, jurorId]);

  if (!editors.length) {
    return null;
  }

  const visible = editors.slice(0, MAX_VISIBLE);
  const overflowCount = editors.length - visible.length;

  const tooltip = (
    <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
      {'Editing:\n' +
        editors
          .map((e) =>
            e.displayName && e.displayName !== e.email
              ? `${e.displayName} (${e.email})`
              : e.email,
          )
          .join('\n')}
    </Box>
  );

  const circleSx = (index, bgcolor) => ({
    width: size,
    height: size,
    fontSize: Math.round(size * 0.45),
    fontWeight: 700,
    bgcolor,
    color: '#fff',
    border: '1.5px solid #fff',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
    marginLeft: index === 0 ? 0 : `-${Math.round(size * 0.3)}px`,
    zIndex: index + 1,
  });

  return (
    <Tooltip title={tooltip} placement="top">
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          ml: 0.5,
          flexShrink: 0,
        }}
      >
        {visible.map((person, index) => (
          <Avatar
            key={person.sessionId || person.email}
            sx={circleSx(index, person.color || '#7b1fa2')}
          >
            {initialsForCollaborator(person.email, person.displayName)}
          </Avatar>
        ))}
        {overflowCount > 0 && (
          <Avatar sx={circleSx(visible.length, '#616161')}>+</Avatar>
        )}
      </Box>
    </Tooltip>
  );
}
